import angular from 'angular';

import { LABOR_COST_CALCULATOR_TYPE } from '../../constants';

import laborCostSettingsUrl from './laborCostSettings.html';

class LaborCostSettingsCtrl {
  constructor(
    Authentication,
    Restangular,
    PersonaCollection,
    LaborCostSettings,
    CurrencyService,
    EventCollection,
    LaborUISchema,
    sdk,
    toTimeFilter,
    $q
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      Restangular,
      PersonaCollection,
      LaborCostSettings,
      CurrencyService,
      EventCollection,
      LaborUISchema,
      sdk,
      toTimeFilter,
      $q,
    });
  }

  isStaging = __STAGING__;

  $onInit() {
    const { PAYROLL_PERIOD_TYPES } = this.sdk('constants');
    this.payrollPeriodTypes = [
      { value: 'WEEKLY', label: PAYROLL_PERIOD_TYPES.WEEKLY },
      { value: 'BI_WEEKLY', label: PAYROLL_PERIOD_TYPES.BI_WEEKLY },
      { value: 'SEMI_MONTHLY', label: PAYROLL_PERIOD_TYPES.SEMI_MONTHLY },
      { value: 'MONTHLY', label: PAYROLL_PERIOD_TYPES.MONTHLY },
    ];

    this.initData();
    this.settingsPromise = null;
    this.overtimeStartsAtOptions = this._getHourOptions();
    this._laborAccess = [];
    this.refreshAdminsAndManagers();

    this.unregisterPersonaCollectionOnChange = this.PersonaCollection.onChange(() => {
      this.refreshAdminsAndManagers();
    });

    this.laborCostCalculatorTypes = Object.values(LABOR_COST_CALCULATOR_TYPE).map((type) => ({
      value: type,
      label: type,
    }));

    this.triggerSubmit = this.submit.bind(this);
    this.triggerReset = this.reset.bind(this);
  }

  $onDestroy() {
    this.unregisterPersonaCollectionOnChange();
  }

  refreshAdminsAndManagers() {
    this.managers = this.PersonaCollection.personas.filter((p) => p.isManager && !p.isAdmin);
    this.admins = this.PersonaCollection.personas.filter((p) => p.isAdmin);
  }

  get selectedCurrency() {
    return this._selectedCurrency;
  }

  set selectedCurrency(val) {
    this._selectedCurrency = val;
    this.laborSettings.currency = val.code;
  }

  async initData() {
    await this.LaborCostSettings.loadData();
    this.reset();
    await this.setInitialCurrency();
  }

  reset() {
    this.LaborCostSettings.reset();
    this.laborSettings = this.LaborCostSettings.settings;

    if (this._initialCurrency) {
      this.selectedCurrency = this._initialCurrency;
    }

    if (this.form) {
      this.form.$setPristine();
      this.form.$setUntouched();
    }
  }

  async submit() {
    if (!this.form) {
      return;
    }

    if (this.form.$invalid) {
      throw new Error('Form invalid');
    }

    if (
      !this.form.$dirty ||
      angular.toJson(this.laborSettings) === angular.toJson(this.LaborCostSettings.savedSettings)
    ) {
      return;
    }

    const needsEventsSync =
      Boolean(this.laborSettings.holidaysEnabled) !==
      Boolean(this.LaborCostSettings.savedSettings.holidaysEnabled);

    await this.LaborCostSettings.save(this.laborSettings);

    if (needsEventsSync) {
      this.EventCollection.syncEvents();
    }

    this._initialCurrency = this.selectedCurrency;
    await this.CurrencyService.setOrgCurrency(this.laborSettings.currency);

    this.form.$setSubmitted();
    this.reset();
  }

  _getHourOptions() {
    let date = new Date();

    date.setHours(0);
    date.setMinutes(0);

    const res = [];
    while (res.length < 96) {
      // 96 is the number of 15-minutes periods in a day
      res.push(this.toTimeFilter(date));
      date = new Date(date.getTime() + 15 * 60 * 1000);
    }

    return res;
  }

  laborOvertimeWeekThresholdChange() {
    const threshold = Number(this.laborSettings._overtimeWeekThresholdHours);
    if (!Number.isNaN(threshold)) {
      this.laborSettings.overtimeWeekThreshold = threshold * 60;
    }
  }

  laborSpreadOfHoursThresholdChanged() {
    const threshold = Number(this.laborSettings._spreadOfHoursThresholdHours);
    if (!Number.isNaN(threshold)) {
      this.laborSettings.spreadOfHoursThreshold = threshold * 60;
    }
  }

  removeFromLaborAccess(persona) {
    const index = this.laborSettings.access.findIndex((p) => p.id === persona.id);
    if (index >= 0) {
      this.laborSettings.access.splice(index, 1);
    }
  }

  async setInitialCurrency() {
    const currency = this.laborSettings.currency
      ? await this.CurrencyService.getCurrencyByCode(this.laborSettings.currency)
      : this.CurrencyService.getDefaultCurrency();

    this._initialCurrency = currency;
    this.selectedCurrency = currency;
  }
}

export default {
  controller: LaborCostSettingsCtrl,
  controllerAs: 'ctrl',
  templateUrl: laborCostSettingsUrl,
  bindings: {
    triggerSubmit: '=?',
    triggerReset: '=?',
  },
};
